import { cn } from "@babybjorn/ui";
import { useRef, type ElementType, type FC, type ReactNode } from "react";
import Button from "../../components/general/Button";
import type { ButtonProps } from "../../components/general/Button/Button";
import { useIsTablet } from "../../hooks/use-is-screensize";
import * as s from "./Hero.module.scss";

type HeroProps = {
	title?: string;
	label?: string;
	subLabel?: string;
	backgroundColor?: string;
	media?: ReactNode;
	mobileMedia?: ReactNode;
	buttonProps?: ButtonProps;
	labelHeadingAs?: ElementType;
	variant: "backgroundColor" | "backgroundImage" | "onlyImage";
	mobileImageRatio?: "1:1" | "5:7";
	inverted?: boolean;
	isInDoubleTextBlock?: boolean;
};

const Hero: FC<HeroProps> = ({
	title,
	label,
	subLabel,
	labelHeadingAs = "h1",
	variant = "backgroundColor",
	inverted,
	backgroundColor: bgcolor = "#f3e4de",
	mobileImageRatio = "1:1",
	media,
	mobileMedia,
	buttonProps,
	isInDoubleTextBlock,
}) => {
	const Heading = labelHeadingAs;
	const desktopRef = useRef<HTMLDivElement | null>(null);
	const isTablet = useIsTablet();

	return (
		<section
			className={cn(s.hero, {
				[s.ratio_5_7]: mobileImageRatio === "5:7",
				[s.doubleTextBlock]: isInDoubleTextBlock,
			})}
			style={{
				backgroundColor: variant === "backgroundColor" ? bgcolor : "none",
			}}
		>
			{media && (variant === "backgroundImage" || variant === "onlyImage") && (
				<div
					ref={desktopRef}
					className={cn(s.background, s.desktopImage, {
						[s.noMobileImage]: !mobileMedia,
					})}
				>
					{isTablet && media}
				</div>
			)}
			{mobileMedia && (
				<div
					className={cn(s.background, s.mobileImage, {
						[s.noDesktopImage]: !media,
					})}
				>
					{!isTablet && mobileMedia}
				</div>
			)}

			{variant !== "onlyImage" && (
				<div
					className={cn(s.overlay, {
						[s.inverted]: inverted,
					})}
				>
					<p className={s.label}>{label}</p>
					<Heading
						className={cn({
							"break-all hyphens-auto": title?.includes("|"),
							"pb-6 lg:pb-8": !subLabel,
							"pb-2": subLabel,
						})}
					>
						{title?.replaceAll("|", "\u00AD")}
					</Heading>

					{subLabel && <h4 className="pb-4 lg:pb-6">{subLabel}</h4>}

					{buttonProps ? (
						<Button
							{...buttonProps}
							variant={inverted ? "inverted" : "secondary"}
						/>
					) : (
						<hr
							className={cn({
								[s.inverted]: inverted,
							})}
						/>
					)}
				</div>
			)}
		</section>
	);
};

export default Hero;
